import { supabase } from "../supabase";

export type WaitlistStatus = "pending" | "allowlisted" | "declined";

export interface WaitlistEntry {
  id: string;
  user_id: string | null;
  email: string;
  status: WaitlistStatus;
  source: string | null;
  note: string | null;
  requested_at: string;
  allowlisted_at: string | null;
  allowlisted_by: string | null;
}

export async function fetchMyWaitlistStatus(): Promise<WaitlistStatus> {
  const { data, error } = await supabase.rpc("get_my_waitlist_status");
  if (error) throw new Error(error.message);
  const value = (data ?? "pending") as WaitlistStatus;
  if (value !== "pending" && value !== "allowlisted" && value !== "declined") {
    return "pending";
  }
  return value;
}

export async function amIPlatformAdmin(userId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("is_platform_admin", {
    uid: userId,
  });
  if (error) throw new Error(error.message);
  return Boolean(data);
}

export async function listWaitlist(): Promise<WaitlistEntry[]> {
  const { data, error } = await supabase.rpc("list_waitlist");
  if (error) throw new Error(error.message);
  return (data ?? []) as WaitlistEntry[];
}

export async function allowlistEmail(email: string): Promise<WaitlistEntry> {
  const { data, error } = await supabase.rpc("allowlist_waitlist_entry", {
    target_email: email,
  });
  if (error) throw new Error(error.message);
  return data as WaitlistEntry;
}

export async function declineEmail(
  email: string,
  reason?: string,
): Promise<WaitlistEntry> {
  const { data, error } = await supabase.rpc("decline_waitlist_entry", {
    target_email: email,
    reason: reason ?? null,
  });
  if (error) throw new Error(error.message);
  return data as WaitlistEntry;
}

// ---------------------------------------------------------------------------
// Platform admin self-management
// ---------------------------------------------------------------------------

export interface PlatformAdminEntry {
  user_id: string;
  email: string | null;
  created_at: string;
  note: string | null;
  granted_by: string | null;
  granted_by_email: string | null;
  can_revoke: boolean;
}

export async function listPlatformAdmins(): Promise<PlatformAdminEntry[]> {
  const { data, error } = await supabase.rpc("list_platform_admins");
  if (error) throw new Error(error.message);
  return (data ?? []) as PlatformAdminEntry[];
}

export async function grantPlatformAdmin(
  email: string,
): Promise<PlatformAdminEntry> {
  const { data, error } = await supabase.rpc("grant_platform_admin", {
    target_email: email,
  });
  if (error) throw new Error(error.message);
  return data as PlatformAdminEntry;
}

export async function revokePlatformAdmin(
  userId: string,
): Promise<PlatformAdminEntry> {
  const { data, error } = await supabase.rpc("revoke_platform_admin", {
    target_user_id: userId,
  });
  if (error) throw new Error(error.message);
  return data as PlatformAdminEntry;
}

// ---------------------------------------------------------------------------
// Platform-wide allowlist kill switch (beta ↔ open)
// ---------------------------------------------------------------------------

export async function getAllowlistMode(): Promise<boolean> {
  const { data, error } = await supabase.rpc("is_allowlist_enabled");
  if (error) throw new Error(error.message);
  return Boolean(data);
}

export async function setAllowlistMode(enabled: boolean): Promise<boolean> {
  const { data, error } = await supabase.rpc("set_allowlist_mode", {
    enabled,
  });
  if (error) throw new Error(error.message);
  return Boolean(data);
}
